import { useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Col, Row } from 'antd';
import { useRouter } from 'next/router';

import { getProducts } from 'actions/products';
import PaginatedGrid from 'components/paginatedGrid';
import Paginator from 'components/paginator';
import ProductsGrid from 'components/productsGrid';
import { useErrorContext } from 'context/error';
import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import { useLocale } from 'hooks';
import { useGrid } from 'hooks/useGrid';
import { useGridVouchers } from 'hooks/useGridVouchers';
import type { AllFilters, TFilter, TProductListPage } from 'types/product-list';
import { TIER_POSITIONS } from 'utils/constants/cms';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { DEFAULT_SORT, type TSort } from 'utils/filters';
import { createPagesArray, getPageProps, updateUrlWithFilters } from 'utils/grid';
import { checkFilters, parseProductsEcommerceGA4 } from 'utils/gtmUtils';
import { COOKIES, DEFAULT_LIMIT, filterTransformer, getCookie } from 'utils/helpers';
import { getDomainValue } from 'utils/domain';
import { normalizeActiveFilters, useFilters, type FilterState } from './use-filters';

import styles from './ProductList.module.css';
import { CamperlabBreadcrumbs } from './camperlab-breadcrumbs';

type ProductsListPageProps = Omit<TProductListPage, 'defaultProps' | 'hrefLangLinks' | 'metatags' | 'recycled' | 'filtersCount'>;

const TierComponent = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TierComponent)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TierComponent)),
  // @ts-ignore
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.TierBannerComponent)),
});
const SeoContent = dynamic(() => import('components/seo-content').then((mod) => mod.SeoContent));
const CamperFilterHeader = dynamic(() => import('./camper-filter-header').then((mod) => mod.CamperFilterHeader));
const CamperFilterPanel = dynamic(() => import('./camper-filter-panel').then((mod) => mod.CamperFilterPanel));
const CamperlabFilters = dynamic(() => import('./camperlab-filters').then((mod) => mod.CamperlabFilters));

export function ProductsListPage({
  firstProducts,
  previousPageProducts,
  nextPageProducts,
  total,
  availableFilters,
  baseAvailable,
  initialPage,
  appliedFilters: initialApplied,
  tiersProps,
  breadcrumbI18n,
  gtmProps,
  currentList,
  camperlab,
  title,
  description,
  autoappliedVoucher,
  urlCanonical,
  hasFiltersInURL: filtersInUrl,
  originalCta,
  seoContent,
  subgridProducts,
  subgridUrlCollection,
  isApp,
}: ProductsListPageProps) {
  const { locale } = useLocale();
  const { spreadError } = useErrorContext();
  const { setContextFilters } = useUser();
  const { t, profileData } = useI18n();
  const router = useRouter();

  const [productsPerPage, setProductsPerPage] = useState(() =>
    createPagesArray({
      initialPage,
      firstProducts,
      previousPageProducts,
      nextPageProducts,
    }),
  );
  const { voucherApplied } = useGridVouchers({ firstProducts, originalCta, autoappliedVoucher });

  const {
    state: filterState,
    dispatch: filterDispatch,
    filters,
    filtersCount,
  } = useFilters({
    all: baseAvailable,
    available: availableFilters,
    initial: initialApplied,
  });
  // Filters
  const [currentTotal, setCurrentTotal] = useState(total);
  const [hasFiltersUrl, setHasFiltersUrl] = useState(filtersInUrl);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const initialCamperlabFilters = useRef<Partial<AllFilters> | null>(null);
  // esto antes se hacia en un useEffect. Mejor setearlo en el primer render y listo. Cuantos menos effects mejor.
  if (camperlab && initialCamperlabFilters.current === null) {
    initialCamperlabFilters.current = JSON.parse(JSON.stringify(initialApplied));
  }

  const { pagesRange, tiersIndexes } = useGrid({ tiersProps, productsPerPage });

  const { query } = router;
  const { target, product } = query;

  const typologyFilterValues = filterState.active?.['typologies'] ? Array.from(filterState.active?.['typologies'].values()).map((f: TFilter) => f.key) : [];

  const totalPages = Math.ceil(currentTotal / DEFAULT_LIMIT);
  const isGridSeeAll = filtersCount === 0 && !hasFiltersUrl;
  const filterPanelClassname = subgridProducts.length === 0 ? styles.filterPanelCol : styles.filterPanelColStatic;

  const handleGetProducts = (
    page: number,
    filters: FilterState['active'],
    sort: TSort,
    limit: number,
    callback: (data: any, newTotal: number) => void,
    sendGTM = true,
  ) => {
    if (page < 0) return;
    const normalizedFilters = normalizeActiveFilters(filters);
    const queryFilters: Record<string, any> = filterTransformer.toQuery(normalizedFilters);

    const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
    const voucher = getCookie(COOKIES.BONDS) || '';
    const voucherDiscount = getDomainValue({ camper: autoVoucher && autoVoucher === AUTO_BONDS.APPLY, nnormal: false, camperlab: false });
    // in getProducts page is not the page but the offset of the list
    const pageOffset = page * DEFAULT_LIMIT;
    // @ts-ignore
    getProducts({
      lang: locale,
      target,
      page: pageOffset,
      filters: queryFilters,
      limit,
      sort,
      product,
      voucherDiscount: voucherDiscount ? voucher : undefined,
      square: getDomainValue({ camper: true, nnormal: false, camperlab: false }),
    })
      .then(({ products: responseProducts, total: responseTotal, availableFilters: newAvailableFilters }) => {
        if (sendGTM) {
          const eventLayerGA4 = {
            event: 'view_item_list',
            nonInteraction: '1',
            ecommerce: {
              currency: profileData?.currencySap || (responseProducts?.length ? responseProducts?.[0]?.prices?.currency : ''),
              items: parseProductsEcommerceGA4(responseProducts, gtmProps?.currentListId, gtmProps?.currentList, page),
            },
          };
          dataLayerHandleEvent(eventLayerGA4);
        }

        const hasFilterInUrl = Object.keys(queryFilters).length > 0;
        updateUrlWithFilters(queryFilters, sort);
        setHasFiltersUrl(hasFilterInUrl);
        setContextFilters(normalizedFilters);
        filterDispatch({ type: 'update_active', new: filters });
        filterDispatch({ type: 'update_available', new: newAvailableFilters });
        filterDispatch({ type: 'set_sort', sort });
        return callback(responseProducts, responseTotal);
      })
      .catch(spreadError);
  };

  const handleLoadPage = (newPageIndex: number, triggerPosition: 'middle' | 'bottom') => {
    if (newPageIndex < 0 || newPageIndex > totalPages - 1) return;

    /**
     * Para evitar fetches duplicados, tenemos que retornar con diferentes condiciones dependiendo de donde viene el evento de carga
     * Si el trigger es el de en medio siempre miramos si la pagina anterior tiene productos.
     * Si el trigger es el de abajo, miramos si existe el array en el index de la pagina. Si existe significa que el trigger anterior ya ha cargado o esta cargando los productos.
     */
    if (triggerPosition === 'middle' && productsPerPage[newPageIndex] && productsPerPage[newPageIndex].length) {
      return;
    }
    if (triggerPosition === 'bottom' && productsPerPage[newPageIndex] && productsPerPage[newPageIndex].length) {
      return;
    }

    // si no existe el array en el index de la pagina añadimos un array vacio
    if (!productsPerPage[newPageIndex]) {
      setProductsPerPage((prev) => {
        const prevCopy = [...prev];
        prevCopy[newPageIndex] = [];
        return prevCopy;
      });
    }
    handleGetProducts(
      newPageIndex,
      filterState.active,
      filterState.sort,
      DEFAULT_LIMIT,
      (response) => {
        setProductsPerPage((prev) => {
          const prevCopy = [...prev];
          prevCopy[newPageIndex] = response;
          return prevCopy;
        });
      },
      true,
    );
  };

  const handleApplyFilters = (filters: FilterState['active'], sort: TSort = DEFAULT_SORT) => {
    setProductsPerPage([[]]);
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    const filtersToUse = { ...filters };

    if (initialCamperlabFilters.current !== null) {
      Object.entries(initialCamperlabFilters.current).forEach(([key, value]) => {
        if (filters[key] === undefined) {
          filtersToUse[key] = new Map(value.map((filter) => [filter.key, filter]));
        } else {
          value.forEach((filter) => {
            if (!filters[key].has(filter.key)) {
              filtersToUse[key].set(filter.key, filter);
            }
          });
        }
      });
    }

    const queryFilters = filterTransformer.toQuery(filtersToUse);
    updateUrlWithFilters(queryFilters, sort);
    handleGetProducts(0, filtersToUse, sort, DEFAULT_LIMIT, (response, newTotal) => {
      setProductsPerPage([response]);
      setCurrentTotal(newTotal);
    });
  };

  // ver si se pueden unificar
  const onClosePanel = (visible: boolean, fakeApply = false) => {
    filterDispatch({ type: 'toggle_open', open: visible });
    // si ya ha cargado los valores filtrados hacemos un loading falso
    if (window.innerWidth < 768) {
      document.querySelector('body').classList.toggle('filterOpen');
    }

    if (fakeApply && productsPerPage.length === 1 && productsPerPage[0].length !== 0) {
      const copy = JSON.parse(JSON.stringify(productsPerPage));
      setProductsPerPage([[]]);
      setTimeout(() => setProductsPerPage(copy), 500);
    }
  };

  // Funciones de filtros
  const handleClickSort = (sortValue: TSort) => {
    handleApplyFilters(filterState.active, sortValue);
  };

  const handleRemoveFilter = ({ parent, key }: { parent: string; key: string }) => {
    const newActiveFilters = { ...filterState.active };
    if (!newActiveFilters[parent]) {
      console.error(`trying to delete filter ${key} on ${parent} but ${parent} does not exist`);
      return;
    }
    newActiveFilters[parent].delete(key);
    handleApplyFilters(newActiveFilters, filterState.sort);
  };

  const handleResetFilters = () => {
    handleApplyFilters({}, 'default');
  };

  return (
    <div className="lab:mb-15">
      <Row>
        {getDomainValue({
          camper: (
            <Col span={24} className={`${styles.productListTitle}`}>
              {tiersProps[TIER_POSITIONS.STANDALONE] ?
                <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE]} typology={typologyFilterValues} />
              : <>
                  <h1 className={`${styles.title} empty:hidden`} dangerouslySetInnerHTML={{ __html: title }} />
                  <h2 className={`${styles.description} empty:hidden`} dangerouslySetInnerHTML={{ __html: description }} />
                </>
              }
            </Col>
          ),
          nnormal: (
            <Col span={24} className={`${styles.productListTitle}`}>
              {tiersProps[TIER_POSITIONS.STANDALONE] ?
                <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE]} typology={typologyFilterValues} />
              : null}
            </Col>
          ),
          camperlab: null,
        })}
        {getDomainValue({
          camper: (
            <>
              <Col span={24} className={styles.filterHeaderCol} id="plp-filter-nav">
                <CamperFilterHeader
                  state={filterState}
                  toggle={() => filterDispatch({ type: 'toggle_open' })}
                  count={filtersCount}
                  onRemoveFilter={handleRemoveFilter}
                  onReset={handleResetFilters}
                  total={currentTotal}
                  onSort={handleClickSort}
                />
              </Col>

              <Col
                xs={{ span: filterState.open ? 24 : 0 }}
                md={{ span: filterState.open ? 8 : 0 }}
                lg={{ span: filterState.open ? 8 : 0 }}
                xl={{ span: filterState.open ? 8 : 0 }}
                xxl={{ span: filterState.open ? 6 : 0 }}
                className={filterPanelClassname}
                id="plp-filter-panel"
                data-open={filterState.open}
              >
                <CamperFilterPanel
                  state={filterState}
                  onApply={handleApplyFilters}
                  filters={filters}
                  onClose={onClosePanel}
                  filtersCount={filtersCount}
                  onSort={handleClickSort}
                  onReset={handleResetFilters}
                  onRemove={handleRemoveFilter}
                />
              </Col>
            </>
          ),
          nnormal: null,
          camperlab: (
            <div className="flex w-full items-baseline justify-between p-2.5">
              <div className="">
                {tiersProps[TIER_POSITIONS.STANDALONE] ?
                  <div className="text-sm font-bold lg:text-xs">
                    <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE]} typology={typologyFilterValues} />
                  </div>
                : <CamperlabBreadcrumbs i18nKeys={breadcrumbI18n} />}
              </div>
              <div className="flex-shrink-0">
                <CamperlabFilters
                  state={filterState}
                  onApply={handleApplyFilters}
                  filters={filters}
                  onClose={onClosePanel}
                  filtersCount={filtersCount}
                  onSort={handleClickSort}
                  onReset={handleResetFilters}
                  onRemove={handleRemoveFilter}
                />
              </div>
            </div>
          ),
        })}
        <Col
          className={`${styles.gridColWrapper} lab:px-2.5`}
          xs={{ span: 24 }}
          md={{ span: getDomainValue({ camper: filterState.open ? 16 : 24, nnormal: 'camper', camperlab: 24 }) }}
          lg={{ span: getDomainValue({ camper: filterState.open ? 16 : 24, nnormal: 'camper', camperlab: 24 }) }}
          xl={{ span: getDomainValue({ camper: filterState.open ? 16 : 24, nnormal: 'camper', camperlab: 24 }) }}
          xxl={{ span: getDomainValue({ camper: filterState.open ? 16 : 24, nnormal: 'camper', camperlab: 24 }) }}
        >
          {getDomainValue({
            camper: null,
            nnormal: (
              <h1 className={styles.gridTitle}>
                {gtmProps.currentListName ?? t('listado.productos', `grid.title.${breadcrumbI18n.target}.${breadcrumbI18n.family}`, 'Todos los zapatos')}
              </h1>
            ),
            camperlab: null,
          })}
          <Row
            className={`${styles.rowGrid} grid`}
            gutter={getDomainValue({
              camper: [0, 0],
              nnormal: [0, 0],
              camperlab: [10, 40],
            })}
          >
            {currentTotal > 0 &&
              productsPerPage.map((products, idx) => {
                const pageProps = getPageProps({
                  products,
                  index: idx,
                  pagesRange,
                  tiersIndexes,
                  tiersProps,
                  TierComponent: TierComponent,
                });
                return (
                  <PaginatedGrid
                    key={`page-${idx + 1}`}
                    page={idx}
                    totalPages={totalPages}
                    products={pageProps.products}
                    currentList={currentList}
                    currentListName={title}
                    placeholder={pageProps.placeHolderArray}
                    locale={locale}
                    colXS={{ span: 12 }}
                    colMD={{ span: getDomainValue({ camper: filterState.open ? 12 : 8, nnormal: 'camper', camperlab: 8 }) as number }}
                    colLG={{ span: getDomainValue({ camper: filterState.open ? 8 : 6, nnormal: 'camper', camperlab: 6 }) as number }}
                    colXL={{ span: getDomainValue({ camper: filterState.open ? 8 : 6, nnormal: 'camper', camperlab: 6 }) as number }}
                    colXXL={{ span: getDomainValue({ camper: filterState.open ? 8 : 6, nnormal: 'camper', camperlab: 6 }) as number }}
                    handleLoadNextPage={handleLoadPage}
                    camperlab={camperlab}
                    recalculating={false}
                    withLabel
                    voucherApplied={voucherApplied}
                    hasModal={true}
                    updatePage={(newPage) => setCurrentPage(newPage)}
                    saveLastProduct={true}
                  />
                );
              })}
          </Row>
        </Col>
      </Row>
      <div className="mt-25 flex flex-col gap-10 px-2.5 empty:hidden">
        {tiersProps[TIER_POSITIONS.STANDALONE] ?
          <CamperlabBreadcrumbs i18nKeys={breadcrumbI18n} />
        : null}
        {getDomainValue({
          camper: null,
          nnormal: null,
          camperlab: (
            <div className="grid max-w-md grid-cols-1 gap-2">
              <h1 className={`m-0 py-0 text-left text-sm uppercase empty:hidden lg:text-xs`} dangerouslySetInnerHTML={{ __html: title }} />
              <h2 className={`m-0 py-0 text-left text-xs empty:hidden`} dangerouslySetInnerHTML={{ __html: description }} />
            </div>
          ),
        })}
      </div>
      <Row>
        {!isApp && tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM] && (
          <Col span={24} className={styles.productListTitle}>
            <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM]} />
          </Col>
        )}
        {getDomainValue({
          camper: subgridProducts.length > 0 && (
            <Row
              className={`${styles.rowGrid} ${styles.rowSubgrid} grid`}
              style={
                {
                  '--background': 'var(--color-subgrid)',
                  'background-color': 'var(--color-subgrid)',
                } as unknown as any
              }
            >
              <Col span={24}>
                <div className={styles.subgridTitle} dangerouslySetInnerHTML={{ __html: t('listado.productos', 'subgrid.title', 'check <i>these</i> out:') }} />
              </Col>
              <ProductsGrid
                origin="grid"
                products={subgridProducts}
                locale={locale}
                colXS={{ span: 12 }}
                colMD={{ span: 8 }}
                colLG={{ span: 6 }}
                colXL={{ span: 6 }}
                colXXL={{ span: 6 }}
                withHover
                withSKU
                withLabel={true}
                currentList="subgrid-products"
                camperlab={false}
                currentListName="subgrid-products"
                recalculating={false}
                voucherApplied={false}
                withModal={false}
                squareImg={true}
              />
              <Col span={24} className={styles.discoverMoreCol}>
                <a className={styles.btnDiscoverMore} href={subgridUrlCollection}>
                  {t('listado.productos', 'subgrid.discover.more', 'Discover more')}
                </a>
              </Col>
            </Row>
          ),
          nnormal: null,
          camperlab: null,
        })}

        {!isApp && tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM_SEO] && <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM_SEO]} />}
        {getDomainValue({
          camper: !isApp && seoContent !== null ? <SeoContent content={seoContent} camperlab={camperlab} /> : null,
          nnormal: null,
          camperlab: null,
        })}
      </Row>
      {totalPages > 0 && isGridSeeAll && (
        <div className={filtersCount === 0 && currentPage !== totalPages ? styles.paginatorDiv : `${styles.paginatorDiv} ${styles.hidden}`}>
          <Paginator totalPages={totalPages} currentPage={currentPage} url={urlCanonical} locale={locale} />
        </div>
      )}
    </div>
  );
}
